'use client';

import React, { useState, type ReactNode } from 'react';
import { ChevronDown, ChevronUp, type LucideIcon } from 'lucide-react';
import { cn } from '@sharpr/ui';

type CollapsedTone = 'brand' | 'positive' | 'negative';

interface FilterShellProps {
  icon: LucideIcon;
  label: string;
  summary?: ReactNode;
  headerExtra?: ReactNode;
  onClearAll?: () => void;
  isCollapsed?: boolean;
  /** Initial chevron expand state (uncontrolled). Defaults to expanded.
   *  The header — including headerExtra — always renders; this only controls
   *  whether the chips start shown. */
  defaultExpanded?: boolean;
  /** Controlled chevron expand state. When provided the shell is controlled and
   *  the parent must drive it via `onExpandedChange`; otherwise it falls back to
   *  uncontrolled `defaultExpanded`. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  collapsedTooltip: string;
  collapsedBadge?: ReactNode;
  collapsedTone?: CollapsedTone;
  onCollapsedClick?: () => void;
  collapsedClickDisabled?: boolean;
  chipsMaxHClass?: string;
  chipsGapClass?: string;
  className?: string;
  children: ReactNode;
}

const TONE_COLORS: Record<CollapsedTone, { active: string; badge: string }> = {
  brand: {
    active: 'text-brand-600 dark:text-brand-400',
    badge: 'bg-brand-500 dark:bg-brand-600',
  },
  positive: {
    active: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-500 dark:bg-emerald-600',
  },
  negative: {
    active: 'text-red-600 dark:text-red-400',
    badge: 'bg-red-500 dark:bg-red-600',
  },
};

export function FilterShell({
  icon: Icon,
  label,
  summary,
  headerExtra,
  onClearAll,
  isCollapsed = false,
  defaultExpanded = true,
  expanded,
  onExpandedChange,
  collapsedTooltip,
  collapsedBadge,
  collapsedTone = 'brand',
  onCollapsedClick,
  collapsedClickDisabled = false,
  chipsMaxHClass = 'max-h-24',
  chipsGapClass = 'gap-1.5',
  className,
  children,
}: FilterShellProps) {
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = expanded ?? internalExpanded;
  const toggleExpanded = () => {
    const next = !isExpanded;
    if (expanded === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
  };

  if (isCollapsed) {
    const tone = TONE_COLORS[collapsedTone];
    const isActive = collapsedBadge != null;
    const tile = (
      <>
        <Icon className="w-5 h-5" />
        {collapsedBadge != null && (
          <span
            className={cn(
              'absolute -top-1 -right-1 w-4 h-4 text-white text-xs rounded-full flex items-center justify-center animate-pulse',
              tone.badge
            )}
          >
            {collapsedBadge}
          </span>
        )}
      </>
    );

    const sharedTileClasses = cn(
      'relative p-2 rounded-lg transition-all duration-200',
      isActive ? tone.active : 'text-gray-400 dark:text-text-quaternary'
    );

    return (
      <div className={cn('group relative flex flex-col items-center', className)}>
        {onCollapsedClick ? (
          <button
            onClick={onCollapsedClick}
            disabled={collapsedClickDisabled}
            className={cn(
              sharedTileClasses,
              'focus:outline-none focus:ring-2 focus:ring-brand-500/20',
              isActive && !collapsedClickDisabled
                ? 'hover:bg-gray-100 dark:hover:bg-bg-overlay hover:scale-110 active:scale-95 cursor-pointer'
                : 'cursor-default'
            )}
            aria-label={collapsedTooltip}
          >
            {tile}
          </button>
        ) : (
          <div className={sharedTileClasses} aria-label={collapsedTooltip}>
            {tile}
          </div>
        )}

        <div
          className={cn(
            'absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-bg-quaternary text-white dark:text-text-primary text-xs rounded',
            'opacity-0 group-hover:opacity-100 transition-opacity duration-200',
            'pointer-events-none whitespace-nowrap z-50',
            'top-1/2 -translate-y-1/2'
          )}
        >
          {collapsedTooltip}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-bg-quaternary" />
        </div>
      </div>
    );
  }

  return (
    <div className={cn('group min-w-0', className)}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={toggleExpanded}
            className="flex items-center gap-1 min-h-[24px] text-xs font-medium text-gray-600 dark:text-text-secondary hover:text-gray-900 dark:hover:text-text-primary transition-colors"
          >
            <Icon className="w-3 h-3" />
            {label}
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {summary}
        </div>

        <div className="flex items-center gap-2">
          {headerExtra}
          {onClearAll && (
            <button
              onClick={onClearAll}
              className="inline-flex items-center min-h-[24px] text-xs text-gray-400 dark:text-text-tertiary hover:text-gray-700 dark:hover:text-text-secondary transition-colors"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      <div
        className={cn(
          // mt only when expanded — a collapsed shell is just its header, with
          // no wasted gap below it (the hidden chips carry the spacing instead).
          // mt-3 gives the header row (e.g. the Confirmed-lineup toggle) a bit
          // more breathing room above the chips; trimmed pb pulls the chips
          // closer to the next filter row below.
          'overflow-hidden transition-all duration-300 ease-in-out',
          isExpanded ? `${chipsMaxHClass} opacity-100 mt-3` : 'max-h-0 opacity-0'
        )}
      >
        <div className="w-full overflow-auto scrollbar-on-group-hover pb-1" style={{ overflowY: 'hidden' }}>
          <div className={cn('flex min-w-max', chipsGapClass)}>{children}</div>
        </div>
      </div>
    </div>
  );
}
